import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { Capacitor } from "@capacitor/core";
import useUserAPIClient from "../../api/user/hooks/useUserAPIClient";
import useTasksAPIClient from "../../api/tasks/hooks/useTasksAPIClient";
import useSubscriptionAPIClient from "../../api/subscription/hooks/useSubscriptionAPIClient";
import useActivitiesAPIClient from "../../api/activities/hooks/useActivitiesAPIClient";
import {
  setLabelFilters,
  setMindset,
  setUserAuthenticated,
  setAuthState,
  setTaskLabels,
  setLoadingUser,
  setUserSubscription,
  setSortOrder,
  setTaskCreationPreference,
} from "../../redux/actions/user";
import { dispatch } from "../../redux/store";
import { setTasks } from "../../redux/actions/tasks";
import { setActivities } from "../../redux/actions/activities";
import { initialisePushNotifications } from "../../helpers/push-notifications";
import waitForLocalNotificationsPermission from "../../notifications/waitForLocalNotificationsPermission";
import setupNotificationsChannel from "../../notifications/setupNotificationsChannel";
import cleanUpExpiredNotifications from "../../notifications/cleanupExpiredNotifications";
import { scheduleNotificationsForAllTasks } from "../../notifications/scheduleNotificationsForAllTasks";
import scheduleEngagementNotifications from "../../notifications/engagement/scheduleEngagementNotifications";
import useMessagesAPIClient from "../../api/messages/hooks/useMessagesAPIClient";
import { setMessages } from "../../redux/actions/messages";
import useRevenueCat from "../../hooks/subscription/useRevenueCat";

const useInitialData = () => {
  const { activeUser } = useSelector((state) => state.user);
  const { getUser, setTimezone } = useUserAPIClient();
  const { getTasks } = useTasksAPIClient();
  const { getSubscriptionStatus } = useSubscriptionAPIClient();
  const { getMessages } = useMessagesAPIClient();
  const { getActivities } = useActivitiesAPIClient();
  const { getSubscriptionInfo, isNative } = useRevenueCat();

  useEffect(() => {
    // Id token is set in useAuthenticate and will trigger a reload of page data when it changes
    if (activeUser) {
      getInitialData().catch((error) => {
        // Errors are already handled in useApiClient (shows offline dialog, etc.)
        // Just log here for debugging - don't let it crash the app
        console.error("Failed to load initial data:", error);
        dispatch(setLoadingUser(false));
      });
    }
  }, [activeUser]);

  const getInitialData = useCallback(async () => {
    console.info("Retrieving initial data");
    dispatch(setLoadingUser(true));

    //Create promises for each API call and load them in parallel
    const getUserPromise = new Promise(async (resolve) => {
      try {
        const { mindset, labels, labelFilters, sortPreference, taskCreationPreference } =
          await getUser();
        console.info("Retrieved user data");
        dispatch(setMindset(mindset));
        dispatch(setLabelFilters(labelFilters));
        dispatch(setTaskLabels(labels));
        if (sortPreference) {
          dispatch(setSortOrder(sortPreference));
        }
        if (taskCreationPreference) {
          dispatch(setTaskCreationPreference(taskCreationPreference));
        }
      } catch (error) {
        console.error("Failed to get user data:", error);
      }
      resolve();
    });
    const getUserSubscriptionPromise = new Promise(async (resolve) => {
      try {
        const subscription = await getSubscriptionStatus();

        // On native platforms, also check RevenueCat for real-time cancellation status.
        // Google Play doesn't always send cancellation webhooks immediately - it may wait
        // until the subscription expires. RevenueCat can detect this state in real-time.
        if (isNative && subscription.plan === "member") {
          const revenueCatInfo = await getSubscriptionInfo();
          if (
            revenueCatInfo &&
            revenueCatInfo.isActive &&
            !revenueCatInfo.willRenew
          ) {
            // User has cancelled via Play Store but still has access until expiration
            subscription.status = "cancelling";
            subscription.cancellingAt = revenueCatInfo.expirationDate;
            console.info(
              "RevenueCat detected subscription will not renew, updating to cancelling state",
            );
          }
        }

        // Handle return from Stripe checkout on web.
        // The webhook may not have been processed yet, so if the backend
        // still reports "free", optimistically set to "member" and poll
        // until the backend catches up.
        // If the user landed on /subscribe, they cancelled checkout — ignore the flag.
        const pendingCheckout = localStorage.getItem("pendingStripeCheckout");
        if (pendingCheckout) {
          localStorage.removeItem("pendingStripeCheckout");
          const returnedToSubscribe =
            window.location.pathname === "/subscribe";

          if (!returnedToSubscribe && subscription.plan === "free") {
            // Optimistically show as subscribed immediately
            dispatch(
              setUserSubscription({ plan: "member", status: "active" }),
            );
            resolve();

            // Poll in the background until backend confirms
            const pollForConfirmation = async () => {
              const maxAttempts = 15; // ~30 seconds at 2s intervals
              for (let i = 0; i < maxAttempts; i++) {
                await new Promise((r) => setTimeout(r, 2000));
                try {
                  const updated = await getSubscriptionStatus();
                  dispatch(setUserSubscription(updated));
                  if (updated.plan !== "free") {
                    console.info(
                      "Stripe webhook confirmed — subscription updated",
                    );
                    return;
                  }
                } catch (err) {
                  console.warn("Subscription poll failed:", err);
                }
              }
              // If we exhausted all attempts, the webhook may have failed.
              // Fall back to the backend's current status.
              console.warn(
                "Stripe webhook not received after polling — falling back to backend status",
              );
              try {
                const fallback = await getSubscriptionStatus();
                dispatch(setUserSubscription(fallback));
              } catch (err) {
                console.error("Final subscription fetch failed:", err);
              }
            };
            pollForConfirmation();
            return; // Already resolved above
          }
        }

        dispatch(setUserSubscription(subscription));
      } catch (error) {
        console.error("Failed to fetch subscription status:", error);
        // Default to free plan on error so user can still use the app
        dispatch(setUserSubscription({ plan: "free", status: "error" }));
      }
      resolve();
    });
    const getTasksPromise = new Promise(async (resolve) => {
      try {
        const tasks = await getTasks();
        console.info("Retrieved tasks data");
        dispatch(setTasks(tasks));

        // TODO this should be put somewhere else I think, feels weird being here
        if (Capacitor.isNativePlatform()) {
          //Once we have got hold of the tasks, we need to set up our notifications
          const notifications = async () => {
            initialisePushNotifications();
            await waitForLocalNotificationsPermission();
            await setupNotificationsChannel();
            await cleanUpExpiredNotifications(tasks);
            await scheduleNotificationsForAllTasks(tasks);
            await scheduleEngagementNotifications();
          };
          void notifications();
        }
      } catch (error) {
        console.error("Failed to get tasks:", error);
      }
      resolve();
    });

    await Promise.all([
      getUserPromise,
      getTasksPromise,
      getUserSubscriptionPromise,
    ]);

    // Load activities after other data
    try {
      const activitiesData = await getActivities();
      console.info("Retrieved activities data");
      dispatch(setActivities(activitiesData || []));
    } catch (error) {
      console.error("Failed to get activities:", error);
    }

    dispatch(setLoadingUser(false));
    dispatch(setUserAuthenticated(true));
    dispatch(setAuthState("authenticated"));

    // Sync user's timezone to the server for timezone-aware server-side logic
    try {
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detectedTimezone) {
        await setTimezone(detectedTimezone);
      }
    } catch (error) {
      console.warn("Failed to sync timezone:", error);
    }

    try {
      const messages = await getMessages();
      dispatch(setMessages(messages));
    } catch (error) {
      console.error("Failed to get messages:", error);
    }
  }, [
    getUser,
    getTasks,
    getActivities,
    getSubscriptionStatus,
    getSubscriptionInfo,
    isNative,
    getMessages,
    setTimezone,
  ]);
};

export default useInitialData;
